import React from "react";
import { useState } from "react";

import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQueryClient } from "react-query";

import { CDN_URL } from "../../services/config";
import classNames from "./../../utils/classNames";
import LoadingWheel from "../Shared/LoadingWheel";
import { TextInput } from "../Shared/Inputs/TextInput";
import SwitchInput from "../Shared/Inputs/SwitchInput";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import useAllMediaAdmin from "../../data/useAllMediaAdmin";
import InsertMediaModal from "../MediaManager/InsertMediaModal";
import { useSocialPostMutations } from "../../data/social/useSocialPostMutations";

interface SinglePostFormFields {
  mediaManagerId?: string;
  useOwnImage?: boolean;
  useOgImage?: boolean;
  message?: string;
  linkUrl?: string;
  asLinkPost?: boolean;
  mediaType?: string;
}

export const SingleOneOffPost = () => {
  const { agencyId } = useParams();
  const queryClient = useQueryClient();
  const { media } = useAllMediaAdmin(agencyId);
  const { create } = useSocialPostMutations(agencyId!);
  const [featuredMediaOpen, setFeaturedMediaOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
  } = useForm<SinglePostFormFields>({
    defaultValues: {
      asLinkPost: false,
      useOgImage: true,
    },
  });

  const useOwnImageValue = watch("useOwnImage");
  const mediaManagerIdValue = watch("mediaManagerId");

  const onSubmit = (data: SinglePostFormFields) => {
    setPosting(true);

    create.mutate({
      ...data,
      useOgImage: !data.useOwnImage,
    }, {
      onSettled: () => {
        setPosting(false);
      }
    });
  };

  const handleFeaturedMediaChange = (_: any, __: any, media: any) => {
    queryClient.refetchQueries("allMedia");

    setTimeout(() => {
      setValue("mediaManagerId", media.id, { shouldDirty: true });
      setFeaturedMediaOpen(false);
    }, 1000);
  };

  const getMediaPreview = (mediaManagerIdValue: string, classes: string) => {
    const m = media.data.find((m: any) => m.id === mediaManagerIdValue);

    if (m != null) {
      return m.type.includes("video") ? (
        <video
          className={classNames(`w-full object-contain`, classes)}
          controls
        >
          <source src={`${CDN_URL}${m.path}`} type={m.type} />
        </video>
      ) : (
        <img
          src={`${CDN_URL}${m.path}`}
          alt={m.altText}
          className={classNames(`w-full object-contain`, classes)}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <>
      {/* @ts-ignore */}
      <InsertMediaModal
        allowVideo={true}
        allowURL={false}
        open={featuredMediaOpen}
        setOpen={setFeaturedMediaOpen}
        onInsert={handleFeaturedMediaChange}
      />
      <div className="mb-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          One Off Social Post
        </h3>
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          // stop Enter submitting the form (but allow new lines in textareas)
          if (e.key === "Enter") {
            const el = e.target as HTMLElement;
            const isTextArea = el.tagName === "TEXTAREA";

            if (!isTextArea) {
              e.preventDefault();
            }
          }
        }}
      >
        <div className="flex flex-col gap-y-4 w-full">
          <div
            className={classNames(
              useOwnImageValue && "hover:bg-gray-200",
              "flex items-center justify-center cursor-pointer h-80 bg-gray-100"
            )}
            onClick={() => {
              useOwnImageValue && setFeaturedMediaOpen(true);
            }}
          >
            {(useOwnImageValue && mediaManagerIdValue) && !media.isLoading && media.data ? (
              getMediaPreview(mediaManagerIdValue!, "max-h-80")
            ) : (
              <div className="w-full h-80 p-1 text-sm bg-gray-100 flex flex-col items-center justify-center">
                <p className="text-center">
                  No Image/Video {!useOwnImageValue ? "Found From Link" : "Chosen"}
                </p>
                {useOwnImageValue && (
                  <p className="text-center">Click to add</p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col justify-between">
            <div className="flex gap-x-4">
              <SwitchInput
                label="Use Own Image"
                register={register}
                registerName={"useOwnImage"}
                setValue={setValue}
                watch={watch}
              />
              <SwitchInput
                label="Link Post"
                registerName="asLinkPost"
                register={register}
                watch={watch}
                setValue={setValue}
              />
            </div>
            {watch("asLinkPost") && (
              <div className="mt-4 text-gray-500">
                Add URL below
              </div>
            )}
          </div>

          <TextInput
            label={"Message"}
            register={register}
            registerName={"message"}
            richtext
            rows={4}
          />
          <TextInput
            label={"Link URL"}
            register={register}
            registerName={"linkUrl"}
          />

          <div className="flex gap-x-2">
            {posting ?
              // @ts-ignore 
              <ButtonInput disabled isSubmit={true} onClick={() => { }}>
                <div className="flex flex-row items-center space-x-2">
                  <LoadingWheel width="w-5" height="h-5" />
                  <span>Posting...</span>
                </div>
              </ButtonInput>
              :
              // @ts-ignore 
              <ButtonInput label="Post" isSubmit={true} onClick={() => { }} />
            }
          </div>
        </div>
      </form>
    </>
  );
};